import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { GoSun } from "react-icons/go";
import { BsMoonStars } from "react-icons/bs";
import Highcharts from 'highcharts';
import HighChartsDarkTheme from '../../utils/HighChartsDarkTheme';
import HighChartsLightTheme from '../../utils/HighChartsLightTheme';
import { createRoot } from 'react-dom/client';
//import { useEDPContext } from "../EDPContext";
import { useTranslation } from "react-i18next";
const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const themeRef = useRef(localStorage.getItem('theme') || 'light');
  const { t, i18n } = useTranslation()
  //const {mapViewRef, setMapViewRef} = useEDPContext();
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      // setTheme(savedTheme);
      themeRef.current = savedTheme
    }
    Highcharts.setOptions(savedTheme === 'dark' ? HighChartsDarkTheme : HighChartsLightTheme);
  }, []);
  const updateTheme = (event) => {
    const newTheme = event.detail;
    themeRef.current = newTheme
  };

  useEffect(() => {
    window.addEventListener("themeChanged", updateTheme);

    return () => {
      window.removeEventListener("themeChanged", updateTheme);
    };
  }, [])
  useEffect(() => {
    if (!document.body.classList.contains('toggle-sidebar')) {

      document.body.className = `theme-${themeRef.current}`;
    }
    else {
      document.body.className = `theme-${themeRef.current}`;
      document.body.classList.add('toggle-sidebar');
    }

  }, [themeRef]);

  const updateHighchartsTheme = (selectedTheme) => {
    const isDarkMode = selectedTheme === "dark";

    // Set global options for Highcharts
    Highcharts.setOptions(isDarkMode ? HighChartsDarkTheme : HighChartsLightTheme);

    Highcharts.charts.forEach((chart) => {
      if (chart) {
        const exportingOptions = chart.options.exporting || {};
        const existingContextButton = exportingOptions.buttons?.contextButton || {};


        const existingText = existingContextButton.text || '';

        // Dynamically update only the stroke color inside the SVG <path>
        const updatedText = existingText
          .replace(/color:\s*#[0-9a-fA-F]+/g, `color: ${isDarkMode ? '#ffffff' : '#000000'}`) // Update color in <span>
          .replace(/stroke="[^"]*"/g, `stroke="${isDarkMode ? '#ffffff' : '#757474'}"`); // Update stroke color in <svg>

        // Remove trailing `color: #xxxxxx;` only if it appears **at the end of the string**
        const cleanedText = updatedText.replace(/color:\s*#[0-9a-fA-F]+;?\s*(<\/span>)?$/, '$1');
        chart.update({
          chart: {
            backgroundColor: isDarkMode ? "#444545" : "#FFFFFF",

          },
          title: {
            style: {
              color: isDarkMode ? '#E0E0E3' : '#333333',
              textTransform: 'uppercase',
              fontSize: '0.72rem',
            },
            useHTML: true
          },
          subtitle: {
            style: {
              color: isDarkMode ? '#E0E0E3' : '#666666',
              textTransform: 'uppercase',
            },
          },
          legend: {
            borderColor: isDarkMode ? "#444444" : "#e4e4e4", // Darker border in dark mode   
            backgroundColor: isDarkMode ? " #1e1e1e" : " #f1f1f1",
            itemStyle: {
              color: isDarkMode ? "#FFFFFF" : "#000000",
            },
            itemHoverStyle: {
              color: isDarkMode ? "#CCCCCC" : "#333333",
            }
          },
          yAxis: {
            labels: {
              style: {
                color: isDarkMode ? "#fff" : "#000",
              },
            },
          },
          exporting: {
            buttons: {
              contextButton: {
                useHTML: true,
                symbolStroke: isDarkMode ? "#FFFFFF" : "#000000",
                theme: {
                  fill: isDarkMode ? "#333333" : "#F0F0F0",
                  stroke: "none",
                  style: {
                    color: isDarkMode ? "#FFFFFF" : "#000000"
                  }
                },
                menuItems: ["downloadPNG", "downloadPDF", "downloadJPEG"],
                align: i18n.language === 'ar' ? 'left' : 'right', // Position based on language
                x: 3,
                text: cleanedText,
              }
            }
          }
        }, false, true); // Apply changes without full redraw

        // 🔥 Manually update legend
        if (chart.legend) {
          chart.legend.update({
            itemStyle: {
              color: isDarkMode ? "#FFFFFF" : "#000000",
            }
          });
          chart.legend.render();
        }

        // 🔥 Manually update exporting button
        setTimeout(() => {
          if (chart.exportSVGElements && chart.exportSVGElements[0]) {
            chart.exportSVGElements[0].attr({
              stroke: isDarkMode ? "#FFFFFF" : "#000000",
              fill: isDarkMode ? "#333333" : "#F0F0F0"
            });
          }
        }, 500); // Delay to ensure elements are available

        chart.reflow();
        chart.redraw();
      }
    });

    setTimeout(() => {
      // const baseLayers = mapViewRef?.map?.basemap?.baseLayers;
      //  const baseLayer = baseLayers?.items?.[0];
      window.dispatchEvent(new Event("resize"));
      // if (baseLayer && !baseLayer.visible) {
      //   baseLayer.visible = true;
      // }
    }, 100);
  };




  // Apply theme on initial load
  useEffect(() => {
    // debugger
    const savedTheme = localStorage.getItem("theme") || "light";
    updateHighchartsTheme(savedTheme);
  }, []);


  // Apply theme when toggling
  // const toggleTheme = () => {
  //     const newTheme = theme === "light" ? "dark" : "light";
  //     setTheme(newTheme);
  //     localStorage.setItem("theme", newTheme);
  //     updateHighchartsTheme(newTheme);
  // };
  const toggleTheme = () => {
    const newTheme = themeRef.current === "light" ? "dark" : "light";
    themeRef.current = newTheme;
    localStorage.setItem("theme", newTheme);
    document.body.className = `theme-${newTheme}`;
    updateHighchartsTheme(newTheme);
    window.dispatchEvent(new CustomEvent("themeChanged", { detail: themeRef.current }));
  };

  return (
    <ThemeContext.Provider value={{ theme: themeRef.current, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Create the ThemeSwitcher component
export const ThemeSwitcher = () => {
  const { theme, toggleTheme } = useTheme();
  const iconContainerRef = useRef(null);
  const iconRootRef = useRef(null);
  const getTheme = () => localStorage.getItem('theme') || 'light';
  const renderIcon = () => {
    const currentTheme = getTheme();
    const icon = currentTheme === 'light'
      ? <BsMoonStars size={18} style={{ color: '#ffffff', cursor: 'pointer' }} />
      : <GoSun size={18} style={{ color: '#ffffff', cursor: 'pointer' }} />;

    if (iconContainerRef.current) {
      if (!iconRootRef.current) {
        iconRootRef.current = createRoot(iconContainerRef.current);
      }
      iconRootRef.current.render(icon);
    }
  };
  const handleClick = () => {
    toggleTheme();     // switches the theme
    renderIcon();      // updates the icon manually
  };
  useEffect(() => {
    renderIcon(); // Initial render
    window.addEventListener('themeChanged', renderIcon); // Listen for global theme changes

    return () => {
      window.removeEventListener('themeChanged', renderIcon);
    };
  }, []);
  return (
    <div className='theme-switcher ' onClick={handleClick}>
      <div ref={iconContainerRef}></div>
    </div>
  );
};